/* eslint-disable max-lines-per-function */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import './skillMapCard.scss';
import ProfilePicPlaceHolder from '../../../../Assets/images/png/profil-pic-placeholder-large.png';
import { SKILL_MAP_DASHBOARD } from '../../../../Constants/strings';
import { handleDeleteAction, handleCloseAction } from './deleteSkillMapUtilis';
import DeleteDialog from './deleteSkillMapDialog';
import EditSkillMap from './EditSkillMap/editSkillMap';
import { AuthContext } from '../../../../Contexts/AuthContext';
import SimpleSnackbar from '../../../../Components/Snackbar/snackbar';
import { useNavigate } from 'react-router-dom';
import { MODULE_NAMES } from '../../../../Constants/modules';

const useStyles = makeStyles({
  root: {
    fontSize: '0.875rem',
    fontFamily: 'mukta-regular',
    marginLeft: '0.5rem',
    marginRight: '0.5rem',
    borderRadius: '0.25rem',
    padding: '0.5rem',
    color: '#484964',
    '&:hover': {
      backgroundColor: '#ECEDF4',
      color: '#6B5CE8',
    },
  },
});

const SkillMapCard = ({
  skillMap,
  setFilteredList,
  setLevelFilteredList,
  handleSnackbar,
  activeLevel,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [cardData, setCardData] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [severity, setSeverity] = useState('');
  const { userProfile } = useContext(AuthContext);
  const permission = userProfile?.permissions
    ? userProfile.permissions[MODULE_NAMES.CONFIGURATION_SKILL] || []
    : [];
  const navigate = useNavigate();
  const photoPrefixAltText = 'Photo';

  const hasAccess = (action) => {
    return permission.includes(action);
  };
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedSkillMap, setSelectedSkillMap] = useState(null);

  useEffect(() => {
    setCardData(skillMap);
  }, [skillMap]);

  const snackbarOpen = () => {
    setOpenSnackbar(true);
    setTimeout(() => {
      setOpenSnackbar(false);
    }, 4000);
  };

  const handleEditSnackbar = (message, snackbarSeverity) => {
    setSnackbarMessage(message);
    setSeverity(snackbarSeverity);
    snackbarOpen();
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = (data) => {
    setSelectedSkillMap(data.skillMapId);
    setIsDialogOpen(true);
  };

  const handleEditOpen = () => {
    setOpenEditDialog(true);
    setAnchorEl(null);
  };

  const handleEditClose = () => {
    setOpenEditDialog(false);
  };

  const handleViewProfile = (emailId) => {
    navigate('/employee-profile', { state: { emailId: emailId } });
  };

  return (
    <>
      <Card className="skill-map-card">
        <CardContent className="card-content">
          <div className="header">
            <div className="skill-info">
              <div
                className={`skill-level ${cardData.isCoreSkill ? 'core-skill' : ''}`}
              >
                {`L${cardData.skillLevel}`}
              </div>
              <Typography variant="h5" component="div" className="skill-name">
                {cardData.skillName}
              </Typography>
            </div>
            <IconButton onClick={handleMenuOpen}>
              <MoreVertIcon className="skill-map-dropdown-icon" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem
                disabled={!hasAccess('update')}
                classes={{ root: classes.root }}
                onClick={handleEditOpen}
              >
                {SKILL_MAP_DASHBOARD.EDIT.BUTTON_TEXT}
              </MenuItem>
              <MenuItem
                disabled={!hasAccess('delete')}
                classes={{ root: classes.root }}
                onClick={() => handleDelete(skillMap)}
              >
                {SKILL_MAP_DASHBOARD.DELETE.BUTTON_TEXT}
              </MenuItem>
              <MenuItem
                classes={{ root: classes.root }}
                onClick={() => handleViewProfile(cardData.userEmailId)}
              >
                {SKILL_MAP_DASHBOARD.VIEW_PROFILE.BUTTON_TEXT}
              </MenuItem>
            </Menu>
          </div>
          {isDialogOpen && selectedSkillMap && (
            <DeleteDialog
              handleCloseAction={handleCloseAction}
              handleDeleteAction={handleDeleteAction}
              selectedItem={selectedSkillMap}
              setIsDialogOpen={setIsDialogOpen}
              setFilteredList={setFilteredList}
              setLevelFilteredList={setLevelFilteredList}
              handleSnackbar={handleSnackbar}
              setAnchorEl={setAnchorEl}
            />
          )}
          <div className="user-info">
            <div className="profile">
              <div className="user-pic">
                <img
                  className="profile-image"
                  src={cardData.photoSignedUrl || ProfilePicPlaceHolder}
                  alt={`${photoPrefixAltText} - ${cardData.userName}`}
                  onError={(event) => {
                    event.target.src = ProfilePicPlaceHolder;
                  }}
                />
              </div>

              <div className="user-name-id-email">
                <div className="user-name">{cardData.userName}</div>
                <div className="user-id">{cardData.userId}</div>
                <div className="user-email">{cardData.userEmailId}</div>
              </div>
            </div>

            <div className="verical-and-dept">
              <div className="field-header">Vertical</div>
              <div className="field-value">{cardData.vertical}</div>

              <div className="field-header">Department</div>
              <div className="field-value">{cardData.department}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {openEditDialog && cardData && (
        <EditSkillMap
          data={cardData}
          handleClose={handleEditClose}
          updateData={setCardData}
          handleSnackbar={handleEditSnackbar}
          setFilteredList={setFilteredList}
          setLevelFilteredList={setLevelFilteredList}
          activeLevel={activeLevel}
        />
      )}

      <SimpleSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={severity}
      />
    </>
  );
};

SkillMapCard.propTypes = {
  skillMap: PropTypes.object.isRequired,
  setFilteredList: PropTypes.func.isRequired,
  setLevelFilteredList: PropTypes.func.isRequired,
  handleSnackbar: PropTypes.func.isRequired,
  activeLevel: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.oneOf([null]),
  ]).isRequired,
};

export default SkillMapCard;
